import { useState, useEffect, useMemo, useCallback } from 'react';
import styles from './DateTimeClock.module.css';

interface DateTimeClockProps {
  variant?: 'institutional' | 'platform';
  compact?: boolean;
}

export function DateTimeClock({ variant = 'institutional', compact = false }: DateTimeClockProps) {
  const [time, setTime] = useState(new Date());
  const [mounted, setMounted] = useState(false);
  const [prevSeconds, setPrevSeconds] = useState(-1);

  useEffect(() => {
    setMounted(true);
    const interval = setInterval(() => {
      setTime(new Date());
    }, 50); // Smoother updates for fluid second hand

    return () => clearInterval(interval);
  }, []);

  const timeData = useMemo(() => {
    const hours = time.getHours();
    const minutes = time.getMinutes();
    const seconds = time.getSeconds();
    const milliseconds = time.getMilliseconds();

    // Smooth calculations for fluid animation
    const smoothSeconds = seconds + milliseconds / 1000;
    const smoothMinutes = minutes + smoothSeconds / 60;
    const smoothHours = (hours % 12) + smoothMinutes / 60;

    // 12-hour format
    const hours12 = hours % 12 || 12;
    const ampm = hours < 12 ? 'AM' : 'PM';

    // Format with leading zeros
    const hoursStr = hours12.toString().padStart(2, '0');
    const minutesStr = minutes.toString().padStart(2, '0');
    const secondsStr = seconds.toString().padStart(2, '0');

    // Calculate rotation angles for analog display
    const hourRotation = smoothHours * 30;
    const minuteRotation = smoothMinutes * 6;
    const secondRotation = smoothSeconds * 6;

    // Progress percentages for arc displays
    const hourProgress = (hours % 12) / 12;
    const minuteProgress = minutes / 60;
    const secondProgress = smoothSeconds / 60;

    return {
      hours: hoursStr,
      minutes: minutesStr,
      seconds: secondsStr,
      ampm,
      hourRotation,
      minuteRotation,
      secondRotation,
      hourProgress,
      minuteProgress,
      secondProgress,
      rawHours: hours,
      rawMinutes: minutes,
      rawSeconds: seconds,
    };
  }, [time]);

  // Track second changes for animation triggers
  useEffect(() => {
    if (timeData.rawSeconds !== prevSeconds) {
      setPrevSeconds(timeData.rawSeconds);
    }
  }, [timeData.rawSeconds, prevSeconds]);

  const dateData = useMemo(() => {
    const dayName = time.toLocaleDateString('en-GB', { weekday: 'long' });
    const dayNum = time.getDate();
    const month = time.toLocaleDateString('en-GB', { month: 'long' });
    const monthShort = time.toLocaleDateString('en-GB', { month: 'short' });
    const year = time.getFullYear();

    return {
      dayName,
      dayNum,
      month,
      monthShort,
      year,
    };
  }, [time.toDateString()]);

  // Generate decorative star points for the clock face
  const generateStarPoints = useCallback((cx: number, cy: number, spikes: number, outerR: number, innerR: number) => {
    const points = [];
    const step = Math.PI / spikes;
    for (let i = 0; i < 2 * spikes; i++) {
      const r = i % 2 === 0 ? outerR : innerR;
      const angle = i * step - Math.PI / 2;
      points.push(`${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`);
    }
    return points.join(' ');
  }, []);

  if (compact) {
    return (
      <div className={`${styles.clockCompact} ${styles[variant]} ${mounted ? styles.mounted : ''}`}>
        {/* Animated border glow */}
        <div className={styles.compactGlow} />

        {/* Corner flourishes */}
        <svg className={styles.compactCornerSvg} data-position="tl" viewBox="0 0 20 20">
          <path d="M0 20 L0 8 Q0 0 8 0 L20 0" fill="none" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="2" cy="2" r="1.5" fill="currentColor"/>
        </svg>
        <svg className={styles.compactCornerSvg} data-position="tr" viewBox="0 0 20 20">
          <path d="M0 0 L12 0 Q20 0 20 8 L20 20" fill="none" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="18" cy="2" r="1.5" fill="currentColor"/>
        </svg>
        <svg className={styles.compactCornerSvg} data-position="bl" viewBox="0 0 20 20">
          <path d="M0 0 L0 12 Q0 20 8 20 L20 20" fill="none" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="2" cy="18" r="1.5" fill="currentColor"/>
        </svg>
        <svg className={styles.compactCornerSvg} data-position="br" viewBox="0 0 20 20">
          <path d="M20 0 L20 12 Q20 20 12 20 L0 20" fill="none" stroke="currentColor" strokeWidth="1.5"/>
          <circle cx="18" cy="18" r="1.5" fill="currentColor"/>
        </svg>

        {/* Shimmer sweep */}
        <div className={styles.compactShimmer} />

        {/* Main horizontal content */}
        <div className={styles.compactHorizontal}>
          {/* Time Section */}
          <div className={styles.compactTimeSection}>
            <div className={styles.compactTimeDisplay}>
              <span className={styles.compactHour}>{timeData.hours}</span>
              <span className={styles.compactColon}>:</span>
              <span className={styles.compactMinute}>{timeData.minutes}</span>
            </div>
            <div className={styles.compactTimeMeta}>
              <span className={styles.compactSecondsLive}>{timeData.seconds}</span>
              <span className={styles.compactPeriod}>{timeData.ampm}</span>
            </div>
          </div>

          {/* Decorative Separator */}
          <div className={styles.compactSeparator}>
            <span className={styles.compactSepLine} />
            <svg className={styles.compactSepStar} viewBox="0 0 24 24">
              <polygon points="12,2 15,9 22,9 16,14 18,21 12,17 6,21 8,14 2,9 9,9" fill="currentColor"/>
            </svg>
            <span className={styles.compactSepLine} />
          </div>

          {/* Date Section */}
          <div className={styles.compactDateSection}>
            <span className={styles.compactDayName}>{dateData.dayName}</span>
            <span className={styles.compactDateFull}>
              {dateData.dayNum} {dateData.monthShort} {dateData.year}
            </span>
          </div>
        </div>

        {/* Ghana tricolor bar */}
        <div className={styles.compactTricolor}>
          <span data-color="red" />
          <span data-color="gold" />
          <span data-color="green" />
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.clock} ${styles[variant]} ${mounted ? styles.mounted : ''}`}>
      {/* Decorative background patterns */}
      <div className={styles.bgPattern}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="gridPattern" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.3" opacity="0.1" />
            </pattern>
            <radialGradient id="centerGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="var(--clock-gold)" stopOpacity="0.15" />
              <stop offset="100%" stopColor="var(--clock-gold)" stopOpacity="0" />
            </radialGradient>
          </defs>
          <rect width="100" height="100" fill="url(#gridPattern)" />
          <circle cx="50" cy="50" r="45" fill="url(#centerGlow)" />
        </svg>
      </div>

      {/* Floating particles */}
      <div className={styles.particles}>
        {[...Array(6)].map((_, i) => (
          <div key={i} className={styles.particle} style={{ '--delay': `${i * 0.5}s`, '--duration': `${3 + i * 0.5}s` } as React.CSSProperties} />
        ))}
      </div>

      {/* Main clock container */}
      <div className={styles.clockBody}>
        {/* Outer decorative ring */}
        <div className={styles.outerRing}>
          <svg viewBox="0 0 200 200">
            <defs>
              <linearGradient id="ringGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="var(--clock-gold)" stopOpacity="0.8" />
                <stop offset="50%" stopColor="var(--clock-primary)" stopOpacity="0.6" />
                <stop offset="100%" stopColor="var(--clock-gold)" stopOpacity="0.8" />
              </linearGradient>
            </defs>
            {/* Decorative outer circle */}
            <circle cx="100" cy="100" r="98" fill="none" stroke="url(#ringGradient)" strokeWidth="1" />
            <circle cx="100" cy="100" r="94" fill="none" stroke="var(--clock-primary)" strokeWidth="0.5" opacity="0.3" />

            {/* Art deco corner decorations */}
            {[0, 90, 180, 270].map((angle) => (
              <g key={angle} transform={`rotate(${angle} 100 100)`}>
                <path
                  d="M 100 5 L 103 15 L 100 12 L 97 15 Z"
                  fill="var(--clock-gold)"
                  opacity="0.8"
                />
              </g>
            ))}
          </svg>
        </div>

        {/* Clock face */}
        <div className={styles.clockFace}>
          <svg viewBox="0 0 200 200" className={styles.clockSvg}>
            <defs>
              {/* Gradients */}
              <linearGradient id="hourHandGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="var(--clock-primary)" />
                <stop offset="100%" stopColor="var(--clock-dark)" />
              </linearGradient>
              <linearGradient id="minuteHandGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="var(--clock-primary)" />
                <stop offset="100%" stopColor="var(--clock-accent)" />
              </linearGradient>
              <linearGradient id="secondHandGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="var(--clock-gold)" />
                <stop offset="100%" stopColor="var(--clock-gold-dark)" />
              </linearGradient>
              <filter id="handShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="1" dy="2" stdDeviation="2" floodColor="rgba(0,0,0,0.3)" />
              </filter>
              <filter id="goldGlow" x="-50%" y="-50%" width="200%" height="200%">
                <feGaussianBlur stdDeviation="2" result="blur" />
                <feFlood floodColor="var(--clock-gold)" result="color" />
                <feComposite in="color" in2="blur" operator="in" result="glow" />
                <feMerge>
                  <feMergeNode in="glow" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {/* Inner decorative circles */}
            <circle cx="100" cy="100" r="88" fill="none" stroke="var(--clock-primary)" strokeWidth="0.5" opacity="0.2" />
            <circle cx="100" cy="100" r="75" fill="none" stroke="var(--clock-gold)" strokeWidth="0.3" opacity="0.3" strokeDasharray="4 4" />

            {/* Hour markers with Roman numerals style */}
            {[...Array(12)].map((_, i) => {
              const angle = (i * 30 - 90) * (Math.PI / 180);
              const outerR = 85;
              const innerR = i % 3 === 0 ? 70 : 78;
              const x1 = 100 + innerR * Math.cos(angle);
              const y1 = 100 + innerR * Math.sin(angle);
              const x2 = 100 + outerR * Math.cos(angle);
              const y2 = 100 + outerR * Math.sin(angle);
              const isQuarter = i % 3 === 0;

              return (
                <g key={i}>
                  <line
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke={isQuarter ? 'var(--clock-gold)' : 'var(--clock-primary)'}
                    strokeWidth={isQuarter ? 3 : 1.5}
                    strokeLinecap="round"
                    opacity={isQuarter ? 1 : 0.6}
                  />
                  {isQuarter && (
                    <circle
                      cx={100 + 65 * Math.cos(angle)}
                      cy={100 + 65 * Math.sin(angle)}
                      r="3"
                      fill="var(--clock-gold)"
                      opacity="0.5"
                    />
                  )}
                </g>
              );
            })}

            {/* Minute markers */}
            {[...Array(60)].map((_, i) => {
              if (i % 5 === 0) return null;
              const angle = (i * 6 - 90) * (Math.PI / 180);
              const x1 = 100 + 82 * Math.cos(angle);
              const y1 = 100 + 82 * Math.sin(angle);
              const x2 = 100 + 85 * Math.cos(angle);
              const y2 = 100 + 85 * Math.sin(angle);
              const isActive = i === timeData.rawSeconds;

              return (
                <line
                  key={i}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={isActive ? 'var(--clock-gold)' : 'var(--clock-primary)'}
                  strokeWidth={isActive ? 2 : 0.5}
                  strokeLinecap="round"
                  opacity={isActive ? 1 : 0.3}
                  className={isActive ? styles.activeMarker : ''}
                />
              );
            })}

            {/* Decorative center star */}
            <polygon
              points={generateStarPoints(100, 100, 8, 12, 6)}
              fill="none"
              stroke="var(--clock-gold)"
              strokeWidth="0.5"
              opacity="0.4"
            />

            {/* Hour hand */}
            <g
              style={{ transform: `rotate(${timeData.hourRotation}deg)`, transformOrigin: '100px 100px' }}
              filter="url(#handShadow)"
            >
              <polygon
                points="97,100 100,45 103,100"
                fill="url(#hourHandGrad)"
              />
              <line x1="100" y1="100" x2="100" y2="45" stroke="var(--clock-gold)" strokeWidth="1" opacity="0.5" />
            </g>

            {/* Minute hand */}
            <g
              style={{ transform: `rotate(${timeData.minuteRotation}deg)`, transformOrigin: '100px 100px' }}
              filter="url(#handShadow)"
            >
              <polygon
                points="98.5,100 100,30 101.5,100"
                fill="url(#minuteHandGrad)"
              />
            </g>

            {/* Second hand */}
            <g
              style={{ transform: `rotate(${timeData.secondRotation}deg)`, transformOrigin: '100px 100px' }}
              filter="url(#goldGlow)"
              className={styles.secondHandGroup}
            >
              <line x1="100" y1="115" x2="100" y2="25" stroke="url(#secondHandGrad)" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="100" cy="25" r="3" fill="var(--clock-gold)" />
            </g>

            {/* Center cap */}
            <circle cx="100" cy="100" r="8" fill="var(--clock-primary)" />
            <circle cx="100" cy="100" r="5" fill="var(--clock-gold)" />
            <circle cx="100" cy="100" r="2" fill="var(--clock-dark)" />
          </svg>
        </div>

        {/* Progress arcs */}
        <div className={styles.progressArcs}>
          <svg viewBox="0 0 200 200">
            {/* Second progress arc */}
            <circle
              cx="100"
              cy="100"
              r="95"
              fill="none"
              stroke="var(--clock-gold)"
              strokeWidth="2"
              strokeLinecap="round"
              strokeDasharray={`${timeData.secondProgress * 597} 597`}
              style={{ transform: 'rotate(-90deg)', transformOrigin: '100px 100px' }}
              opacity="0.6"
              className={styles.progressArc}
            />
          </svg>
        </div>
      </div>

      {/* Digital time display */}
      <div className={styles.digitalDisplay}>
        <div className={styles.digitalInner}>
          <div className={styles.timeSegment}>
            <div className={styles.digitPair}>
              <span className={styles.digit} data-value={timeData.hours[0]}>{timeData.hours[0]}</span>
              <span className={styles.digit} data-value={timeData.hours[1]}>{timeData.hours[1]}</span>
            </div>
            <span className={styles.label}>HOURS</span>
          </div>

          <div className={styles.separator}>
            <span className={styles.sepDot} />
            <span className={styles.sepDot} />
          </div>

          <div className={styles.timeSegment}>
            <div className={styles.digitPair}>
              <span className={styles.digit} data-value={timeData.minutes[0]}>{timeData.minutes[0]}</span>
              <span className={styles.digit} data-value={timeData.minutes[1]}>{timeData.minutes[1]}</span>
            </div>
            <span className={styles.label}>MINUTES</span>
          </div>

          <div className={styles.separator}>
            <span className={styles.sepDot} />
            <span className={styles.sepDot} />
          </div>

          <div className={styles.timeSegment}>
            <div className={styles.digitPair}>
              <span className={`${styles.digit} ${styles.digitSeconds}`} data-value={timeData.seconds[0]}>{timeData.seconds[0]}</span>
              <span className={`${styles.digit} ${styles.digitSeconds}`} data-value={timeData.seconds[1]}>{timeData.seconds[1]}</span>
            </div>
            <span className={styles.label}>SECONDS</span>
          </div>

          <div className={styles.ampmBadge}>
            <span>{timeData.ampm}</span>
          </div>
        </div>
      </div>

      {/* Date display */}
      <div className={styles.dateDisplay}>
        <div className={styles.dateInner}>
          <div className={styles.dayNameWrap}>
            <span className={styles.dayNameText}>{dateData.dayName}</span>
          </div>
          <div className={styles.dateDetails}>
            <span className={styles.dayNumber}>{dateData.dayNum}</span>
            <div className={styles.monthYearStack}>
              <span className={styles.monthText}>{dateData.month}</span>
              <span className={styles.yearText}>{dateData.year}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Ghana tricolor footer */}
      <div className={styles.tricolorBar}>
        <div className={styles.tricolorSegment} data-color="red" />
        <div className={styles.tricolorSegment} data-color="gold">
          <svg viewBox="0 0 24 24" className={styles.blackStar}>
            <polygon points="12,2 15,9 22,9 16,14 18,21 12,17 6,21 8,14 2,9 9,9" fill="currentColor" />
          </svg>
        </div>
        <div className={styles.tricolorSegment} data-color="green" />
      </div>
    </div>
  );
}
